import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  CircularProgress,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper as MuiPaper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setEventcustomisation } from "../../redux/slices/eventcustomisationsSlice";
import { setPlaylist } from "../../redux/slices/playlistSlice";
import { setLayout } from "../../redux/slices/layoutSlice";
import { setMeeting } from "../../redux/slices/meetingSlice";
import { setDmz } from "../../redux/slices/dmzSlice";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: "92%", md: "80%" },
  maxHeight: { xs: "92vh", md: "90vh" },
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: { xs: 2, sm: 3, md: 4 },
  overflowY: "auto",
};

const ClientUrlModal = ({ isOpen, onClose, emmsId, emmsheaders, headers }) => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [existingClientUrls, setExistingClientUrls] = useState([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const api = localStorage.getItem("api");
  const dispatch = useDispatch();
  // Get DMZ data from Redux
  const dmzRecords = useSelector((state) => state?.dmz?.dmz);
  const meetingspaces = useSelector(
    (state) => state?.allmeetingspaces?.allmeetingspaces
  );

  // Fetch all schedules/meetings and existing client URLs on open
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    setEvents([]);
    setSelectedIds([]);
    setResponse(null);
    setExistingClientUrls([]);

    // Fetch events
    const eventsRequestBody = {
      path: "meeting/contentai",
      query: {},
      emmsId: emmsId,
      body: {},
      requestType: "GET",
      headers: emmsheaders?.headers,
    };

    // Fetch existing client URLs
    const clientUrlsRequestBody = {
      path: "clienturl",
      query: {},
      emmsId: emmsId,
      body: {},
      requestType: "GET",
      headers: emmsheaders?.headers,
    };
    const dmzRequestBody = {
      path: "list/dmz",
      query: {},
      emmsId: emmsId,
      body: {},
      requestType: "GET",
      headers: emmsheaders?.headers,
    };

    Promise.all([
      axios.post(api + "/emms/piggyback", eventsRequestBody, headers),
      axios.post(api + "/emms/piggyback", clientUrlsRequestBody, headers),
      axios.post(api + "/emms/piggyback", dmzRequestBody, headers),
    ])
      .then(([eventsRes, clientUrlsRes, dmzRes]) => {
        dispatch(setDmz(dmzRes?.data?.data));
        setEvents(eventsRes.data || []);
        setExistingClientUrls(clientUrlsRes?.data || []);
      })
      .catch(() => {
        setError("Failed to fetch data.");
      })
      .finally(() => setLoading(false));
  }, [isOpen, emmsId, emmsheaders, headers]);

  const handleToggle = (id) => {
    // Check if this event already has a client URL
    const hasExistingUrl = existingClientUrls?.some(
      (clientUrl) => clientUrl.eventIds && clientUrl.eventIds.includes(id)
    );

    if (hasExistingUrl) {
      // Don't allow selection if URL already exists
      return;
    }

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const getExistingUrlForEvent = (eventId) => {
    const clientUrl = existingClientUrls?.find(
      (clientUrl) =>
        clientUrl?.eventIds && clientUrl?.eventIds?.includes(eventId)
    );
    return clientUrl?.url || null;
  };
  const handleDataRefresh = () => {
    const eventsCustomisationRequestBody = {
      path: "list/eventcustomisations",
      query: {},
      emmsId: emmsId,
      body: {},
      requestType: "GET",
      headers: emmsheaders?.headers,
    };

    // Fetch existing client URLs
    const playlistRequestBody = {
      path: "playlist",
      query: {},
      emmsId: emmsId,
      body: {},
      requestType: "GET",
      headers: emmsheaders?.headers,
    };
    const layoutRequestBody = {
      path: "layout",
      query: {},
      emmsId: emmsId,
      body: {},
      requestType: "GET",
      headers: emmsheaders?.headers,
    };
    const meetingRequestBody = {
      path: "meeting/contentai",
      query: {},
      emmsId: emmsId,
      body: {},
      requestType: "GET",
      headers: emmsheaders?.headers,
    };

    Promise.all([
      axios.post(
        api + "/emms/piggyback",
        eventsCustomisationRequestBody,
        headers
      ),
      axios.post(api + "/emms/piggyback", playlistRequestBody, headers),
      axios.post(api + "/emms/piggyback", layoutRequestBody, headers),
      axios.post(api + "/emms/piggyback", meetingRequestBody, headers),
    ])
      .then(([eventCustomisationRes, playlistRes, layoutRes, meetingRes]) => {
        dispatch(setEventcustomisation(eventCustomisationRes?.data));
        dispatch(setPlaylist(playlistRes?.data));
        dispatch(setLayout(layoutRes?.data));
        dispatch(setMeeting(meetingRes?.data));
        toast.success("Events have been updated", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .catch(() => {
        toast.error("Error while updating", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setError("Failed to fetch data.");
      })
      .finally(() => setLoading(false));
  };

  const handleSend = async (clientURLId, affectedIds) => {
    setLoading(true);
    setError(null);
    setResponse(null);

    const requestBody = {
      path: "clienturl",
      query: {},
      emmsId: emmsId,
      body: {
        ...(clientURLId ? { clientURLId } : {}),
        eventIds:
          affectedIds && affectedIds.length > 0 ? affectedIds : selectedIds,
      },
      requestType: "POST",
      headers: emmsheaders?.headers,
    };
    const api = localStorage.getItem("api");
    axios
      .post(api + "/emms/piggyback", requestBody, headers)
      .then((res) => {
        setResponse(res?.data);
      })
      .catch((err) => {
        console.log(err);
        setError("Failed to send event IDs.");
      })
      .finally(() => setLoading(false));
  };

  const getRoomName = (event) => {
    const room = meetingspaces?.find((space) => space.id === event?.roomId);
    return room?.name || "-";
  };

  const getGroupRoomLabel = (events) => {
    const names = [
      ...new Set(
        (events || [])
          .map((event) => getRoomName(event))
          .filter((name) => name && name !== "-")
      ),
    ];

    if (names.length === 0) return "-";
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1} more`;
  };

  const getClientUrlObjForEvent = (eventId) => {
    return existingClientUrls?.find(
      (clientUrl) => clientUrl.eventIds && clientUrl.eventIds.includes(eventId)
    );
  };

  const getDmzIdForEvent = (eventId) => {
    const clientUrlObj = existingClientUrls?.find(
      (clientUrl) => clientUrl?.eventIds && clientUrl.eventIds.includes(eventId)
    );

    if (clientUrlObj && clientUrlObj?.dmzId) {
      if (typeof clientUrlObj.dmzId === "string") {
        return clientUrlObj.dmzId;
      }

      if (
        typeof clientUrlObj?.dmzId === "object" &&
        clientUrlObj?.dmzId !== null
      ) {
        if (clientUrlObj?.dmzId?.hasOwnProperty(eventId)) {
          return clientUrlObj.dmzId[eventId];
        }

        if (typeof eventId === "number") {
          const stringEventId = eventId.toString();

          if (clientUrlObj.dmzId.hasOwnProperty(stringEventId)) {
            return clientUrlObj.dmzId[stringEventId];
          }
        }

        if (typeof eventId === "string") {
          const numEventId = parseInt(eventId);

          if (clientUrlObj.dmzId.hasOwnProperty(numEventId)) {
            return clientUrlObj.dmzId[numEventId];
          }
        }

        return null;
      }
    }

    const dmzRecord = dmzRecords?.find(
      (dmz) => dmz.meetingCustomisationId === eventId
    );

    return dmzRecord?.id || null;
  };

  const handleViewClick = (event) => {
    setSelectedEvent(event);
    setViewDialogOpen(true);
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleApproval = async (eventId, approvalStatus) => {
    const clientUrlObj = getClientUrlObjForEvent(eventId);
    if (!clientUrlObj?.id) {
      console.error("No client URL ID found for this event.");
      return;
    }

    setLoading(true);
    const api = localStorage.getItem("api");

    const requestBody = {
      path: "clienturl/approval",
      query: { clientURLId: clientUrlObj.id },
      emmsId: emmsId,
      body: {
        approved: approvalStatus,
      },
      requestType: "PUT",
      headers: emmsheaders?.headers,
    };
    try {
      const response = await axios.post(
        api + "/emms/piggyback",
        requestBody,
        headers
      );

      setExistingClientUrls((prev) =>
        prev.map((clientUrl) => {
          if (clientUrl.id === clientUrlObj.id) {
            return {
              ...clientUrl,
              approved: approvalStatus,
            };
          }
          return clientUrl;
        })
      );

      toast.success("Changes will be reflected soon", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      setTimeout(() => {
        handleDataRefresh();
      }, [60000]);
    } catch (error) {
      console.error("Approval error:", error);
      console.error(
        `Failed to ${approvalStatus ? "approve" : "reject"} client URL.`
      );
      toast.error("Error approving content ", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (eventId) => {
    const clientUrlObj = getClientUrlObjForEvent(eventId);
    if (!clientUrlObj?.id) {
      console.error("No client URL ID found for this event.");
      return;
    }

    const affectedEvents = events.filter((event) => {
      const eventClientUrlObj = getClientUrlObjForEvent(event.id);
      return eventClientUrlObj?.id === clientUrlObj.id;
    });

    setPendingApproval({
      eventId,
      approvalStatus: true,
      affectedEvents,
      clientUrlId: clientUrlObj.id,
      type: "approve", // 👈 add this
    });
    setApprovalDialogOpen(true);
  };
  const handleGeneration = (eventId) => {
    const clientUrlObj = getClientUrlObjForEvent(eventId);
    if (!clientUrlObj?.id) {
      console.error("No client URL ID found for this event.");
      return;
    }
    const affectedEvents = events.filter((event) => {
      const eventClientUrlObj = getClientUrlObjForEvent(event.id);
      return eventClientUrlObj?.id === clientUrlObj.id;
    });

    setPendingApproval({
      eventId,
      affectedEvents,
      clientUrlId: clientUrlObj.id,
      type: "update",
    });
    setApprovalDialogOpen(true);
  };

  const handleReject = (eventId) => {
    const clientUrlObj = getClientUrlObjForEvent(eventId);
    if (!clientUrlObj?.id) {
      console.error("No client URL ID found for this event.");
      return;
    }

    const affectedEvents = events.filter((event) => {
      const eventClientUrlObj = getClientUrlObjForEvent(event.id);
      return eventClientUrlObj?.id === clientUrlObj.id;
    });

    setPendingApproval({
      eventId,
      approvalStatus: false,
      affectedEvents,
      clientUrlId: clientUrlObj.id,
      type: "reject",
    });
    setApprovalDialogOpen(true);
  };

  const handleApprovalConfirm = () => {
    if (!pendingApproval) return;

    if (
      pendingApproval.type === "approve" ||
      pendingApproval.type === "reject"
    ) {
      handleApproval(pendingApproval.eventId, pendingApproval.approvalStatus);
    } else if (pendingApproval.type === "update") {
      handleSend(
        pendingApproval.clientUrlId,
        pendingApproval.affectedEvents.map((e) => e.id)
      );
    }

    setApprovalDialogOpen(false);
    setPendingApproval(null);
  };

  const handleApprovalCancel = () => {
    setApprovalDialogOpen(false);
    setPendingApproval(null);
  };

  const groupEventsByClientUrl = () => {
    const grouped = {};
    const ungrouped = [];

    events.forEach((event) => {
      const clientUrlObj = getClientUrlObjForEvent(event.id);

      if (clientUrlObj?.id) {
        if (!grouped[clientUrlObj.id]) {
          grouped[clientUrlObj.id] = {
            clientUrlId: clientUrlObj.id,
            url: clientUrlObj.url,
            approved: clientUrlObj.approved,
            events: [],
          };
        }
        grouped[clientUrlObj.id].events.push(event);
      } else {
        ungrouped.push(event);
      }
    });

    return { grouped, ungrouped };
  };

  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroupExpansion = (clientUrlId) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clientUrlId)) {
        newSet.delete(clientUrlId);
      } else {
        newSet.add(clientUrlId);
      }
      return newSet;
    });
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Box sx={style}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {response
                ? "Client URL generated"
                : "Select Events to Generate Client URL"}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box textAlign="center" py={5}>
              <CircularProgress />
              <Typography variant="body2" mt={2}>
                Loading...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : response ? (
            <Box py={1}>
              <Typography
                variant="subtitle1"
                color="success.main"
                sx={{ mb: 1.5 }}
              >
                Share this link with the client
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.25,
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ flex: 1, minWidth: 0, wordBreak: "break-all" }}
                >
                  {response?.generatedClientURL || "URL not found in response."}
                </Typography>
                <IconButton
                  size="small"
                  color="primary"
                  title="Copy URL"
                  onClick={() =>
                    navigator.clipboard.writeText(
                      response?.generatedClientURL || ""
                    )
                  }
                >
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Box>
              {selectedIds.length > 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Selected Events:
                </Typography>
              ) : null}
              <List disablePadding>
                {events
                  .filter((e) => selectedIds.includes(e.id))
                  .map((event) => (
                    <ListItem
                      key={event.id}
                      disableGutters
                      sx={{ borderTop: "1px solid", borderColor: "divider" }}
                    >
                      <ListItemText
                        primary={
                          event.name || event.title || `Event ${event.id}`
                        }
                        secondary={event.user || "-"}
                      />
                    </ListItem>
                  ))}
              </List>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button variant="outlined" color="error" onClick={onClose}>
                  Close
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <TableContainer
                component={MuiPaper}
                sx={{
                  maxHeight: { xs: "55vh", md: "60vh" },
                  overflow: "auto",
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 800,
                    "& th, & td": { whiteSpace: "nowrap" },
                    "& .MuiButton-root": { whiteSpace: "nowrap" },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Event Name</TableCell>
                      <TableCell>Room</TableCell>
                      <TableCell>Client URL</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Client Action</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const { grouped, ungrouped } = groupEventsByClientUrl();
                      const rows = [];

                      // Add grouped events
                      Object.values(grouped).forEach((group) => {
                        const isExpanded = expandedGroups.has(
                          group.clientUrlId
                        );
                        const hasDmzId = group.events.some(
                          (event) => !!getDmzIdForEvent(event.id)
                        );
                        const hasExistingUrl = !!group.url;

                        // Get the first event for group display
                        const firstEvent = group.events[0];

                        // Main group row
                        rows.push(
                          <TableRow
                            key={`group-header-${group.clientUrlId}`}
                            sx={{
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                edge="start"
                                checked={group.events.every((event) =>
                                  selectedIds.includes(event.id)
                                )}
                                indeterminate={
                                  group.events.some((event) =>
                                    selectedIds.includes(event.id)
                                  ) &&
                                  !group.events.every((event) =>
                                    selectedIds.includes(event.id)
                                  )
                                }
                                disabled={hasExistingUrl}
                                onChange={(e) => {
                                  if (hasExistingUrl) return;
                                  if (e.target.checked) {
                                    setSelectedIds((prev) => [
                                      ...new Set([
                                        ...prev,
                                        ...group.events.map((e) => e.id),
                                      ]),
                                    ]);
                                  } else {
                                    setSelectedIds((prev) =>
                                      prev.filter(
                                        (id) =>
                                          !group.events
                                            .map((e) => e.id)
                                            .includes(id)
                                      )
                                    );
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    toggleGroupExpansion(group.clientUrlId)
                                  }
                                  sx={{ p: 0.5 }}
                                >
                                  {isExpanded ? "▼" : "▶"}
                                </IconButton>
                                <Box>
                                  <Typography variant="body2">
                                    {firstEvent.name ||
                                      firstEvent.title ||
                                      `Event ${firstEvent.id}`}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    +{group.events.length - 1} linked events
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {getGroupRoomLabel(group.events)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              {group.url ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    title={group.url}
                                    sx={{ flex: 1, minWidth: 0 }}
                                  >
                                    {group.url}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    title="Copy URL"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(group.url);
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="inherit" />
                                  </IconButton>
                                </Box>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell>
                              {group.approved === true ||
                              group.approved === "true" ? (
                                <Typography color="success.main">
                                  Approved
                                </Typography>
                              ) : group.approved === false ||
                                group.approved === "false" ? (
                                <Typography color="error.main">
                                  Rejected
                                </Typography>
                              ) : (
                                <Typography color="warning.main">
                                  Pending
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography
                                color={hasDmzId ? "success.main" : "error.main"}
                                sx={{ fontWeight: "bold" }}
                              >
                                {hasDmzId ? "✓" : "✗"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={1}>
                                {!(
                                  group.approved === true ||
                                  group.approved === "true" ||
                                  group.approved === false ||
                                  group.approved === "false"
                                ) && (
                                  <>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      disabled={!hasDmzId}
                                      onClick={() =>
                                        handleApprove(firstEvent.id)
                                      }
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={!hasDmzId}
                                      onClick={() =>
                                        handleReject(firstEvent.id)
                                      }
                                    >
                                      Reject
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleGeneration(firstEvent.id);
                                      }}
                                    >
                                      Update URL
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() =>
                                    toggleGroupExpansion(group.clientUrlId)
                                  }
                                >
                                  {isExpanded ? "Hide" : "Show"}
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );

                        // Expanded event rows
                        if (isExpanded) {
                          group.events.forEach((event, index) => {
                            const hasDmzId = !!getDmzIdForEvent(event.id);

                            rows.push(
                              <TableRow
                                key={`group-event-${group.clientUrlId}-${event.id}`}
                                sx={{
                                  borderLeft: "1px solid #e0e0e0",
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    edge="start"
                                    checked={selectedIds.includes(event.id)}
                                    disabled={hasExistingUrl}
                                    onChange={() => handleToggle(event.id)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      ↳
                                    </Typography>
                                    <Typography variant="body2">
                                      {event.name ||
                                        event.title ||
                                        `Event ${event.id}`}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{getRoomName(event)}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Shared URL
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Group status
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    color={
                                      hasDmzId ? "success.main" : "error.main"
                                    }
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    {hasDmzId ? "✓" : "✗"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="text"
                                    disabled={!hasDmzId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewClick(event);
                                    }}
                                  >
                                    View
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          });
                        }
                      });

                      // Add ungrouped events
                      ungrouped.forEach((event) => {
                        const hasDmzId = !!getDmzIdForEvent(event.id);
                        const hasExistingUrl = !!getExistingUrlForEvent(
                          event.id
                        );

                        rows.push(
                          <TableRow
                            key={`ungrouped-${event.id}`}
                            sx={{
                              cursor: hasExistingUrl
                                ? "not-allowed"
                                : "pointer",
                            }}
                            onClick={() => handleToggle(event.id)}
                            hover={!hasExistingUrl}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                edge="start"
                                checked={selectedIds.includes(event.id)}
                                disabled={hasExistingUrl}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => handleToggle(event.id)}
                              />
                            </TableCell>
                            <TableCell>
                              {event.name || event.title || `Event ${event.id}`}
                            </TableCell>
                            <TableCell>{getRoomName(event)}</TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              {getExistingUrlForEvent(event.id) ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                >
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    title={getExistingUrlForEvent(event.id)}
                                    sx={{ flex: 1, minWidth: 0 }}
                                  >
                                    {getExistingUrlForEvent(event.id)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    title="Copy URL"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(
                                        getExistingUrlForEvent(event.id)
                                      );
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="inherit" />
                                  </IconButton>
                                </Box>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography color="text.secondary">
                                No URL
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                color={hasDmzId ? "success.main" : "error.main"}
                                sx={{ fontWeight: "bold" }}
                              >
                                {hasDmzId ? "✓" : "✗"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="text"
                                disabled={!hasDmzId}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewClick(event);
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      });

                      if (rows.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Typography>No events found.</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return rows;
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: { xs: "block", sm: "none" },
                  textAlign: "center",
                  mt: 1,
                }}
              >
                Swipe sideways to see all columns
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexDirection={{ xs: "column-reverse", sm: "row" }}
                gap={1}
                mt={2}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={selectedIds.length === 0}
                  onClick={() => handleSend()}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Generate Client URL
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: { xs: "none", sm: "block" } }}
                >
                  {selectedIds.length} selected
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleViewDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Event Details
          <IconButton
            aria-label="close"
            onClick={handleViewDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1">
                  <strong>Event Name:</strong>{" "}
                  {selectedEvent.name ||
                    selectedEvent.title ||
                    `Event ${selectedEvent.id}`}
                </Typography>
                <Typography variant="body1">
                  <strong>Event ID:</strong> {selectedEvent.id}
                </Typography>
                {getDmzIdForEvent(selectedEvent.id) && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "black",
                      borderRadius: 1,
                    }}
                  >
                    <strong>DMZ ID:</strong>{" "}
                    {getDmzIdForEvent(selectedEvent.id)}
                  </Typography>
                )}
              </Box>

              {getDmzIdForEvent(selectedEvent.id) &&
                (() => {
                  const dmzRecord = dmzRecords?.find(
                    (dmz) => dmz.id === getDmzIdForEvent(selectedEvent.id)
                  );
                  const imageUrl = dmzRecord?.customisations?.[0]?.imageUrl;

                  if (imageUrl) {
                    const isVideo = /\.(mp4|avi|mov)$/i.test(imageUrl);
                    return (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Client Uploaded Content
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            mt: 2,
                          }}
                        >
                          {isVideo ? (
                            <video
                              controls
                              style={{
                                width: "100%",
                                maxWidth: "400px",
                                height: "auto",
                                borderRadius: "10px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <source src={imageUrl} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <img
                              src={imageUrl}
                              alt="DMZ Customisation"
                              style={{
                                width: "100%",
                                maxWidth: "400px",
                                height: "auto",
                                objectFit: "cover",
                                borderRadius: "10px",
                                border: "1px solid #e0e0e0",
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleViewDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approvalDialogOpen}
        onClose={handleApprovalCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Approval
          <IconButton
            aria-label="close"
            onClick={handleApprovalCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {pendingApproval && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {pendingApproval.approvalStatus ? "Approve" : "Reject"} Linked
                Events
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This action will affect all{" "}
                {pendingApproval.affectedEvents.length} linked events.
              </Typography>

              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                {pendingApproval.affectedEvents.map((event, index) => {
                  return (
                    <Box
                      key={event.id}
                      sx={{
                        p: 1.5,
                        borderBottom:
                          index < pendingApproval.affectedEvents.length - 1
                            ? "1px solid"
                            : "none",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">
                        {event.name || event.title || `Event ${event.id}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Host: {event.user || "N/A"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            onClick={handleApprovalCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApprovalConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientUrlModal;
